/**
 * Admin → Candidates management (Phase 1 of candidate-attribution).
 *
 * Lists the tenant's candidates and lets an admin create/edit them, including
 * the per-candidate reach editor (whole-LD / subregion) and social links.
 *
 * Security is enforced server-side; this view mirrors the rules so the UI
 * degrades sensibly rather than provoking a 403:
 *   - Reading the list is ANALYTICS_READ_ROLES; create/edit is
 *     {ADMIN, ORG_MANAGER}; the nav item is gated to the latter.
 *   - Only an ADMIN may assign region scopes. For an ORG_MANAGER the reach
 *     editor renders read-only with a hint and `scopes` is omitted from the
 *     payload entirely, so their edits never clobber existing reach.
 *
 * Three domain rules are load-bearing and visible in the UI:
 *   - **Deactivate, never delete.** Historical knock leans resolve their
 *     candidate by id; the row must survive. There is no DELETE endpoint and
 *     this view must never imply one.
 *   - **`slug` is immutable after create.** It is what history resolves
 *     through — repurposing one silently re-attributes past knocks. The field
 *     is read-only in edit mode with a hint saying why.
 *   - **Zero scopes means county-wide, not unscoped.** Rendered explicitly;
 *     a blank cell would read as missing data (plan §5.1).
 *
 * ⚠️ `GET /candidates` returns the LIGHT `CandidateListItem` (no
 * bio/headshot/urls/socials). The edit drawer therefore re-fetches the full
 * `CandidateRead` by id before hydrating — hydrating from a list row would
 * blank those fields on save, since PATCH treats a present `socials: []` as
 * replace-all.
 *
 * Styling matches the other admin views (semantic Tailwind tokens:
 * bg-bg-base/bg-bg-raised, text-fg-*, bg-accent, status-*).
 *
 * Mobile: field-facing admins do this from a phone. The table collapses to
 * cards below `md` via `useIsMobile` — a structural swap, which is what that
 * hook is for. (The shared `DataTable`'s `mobileMode` is not usable here — it
 * takes a `useListQuery` result bound to a paginated `{items,page,total}`
 * envelope, and `GET /candidates` returns a bare list. `UsersAdminView`
 * hand-rolls its table for the same reason.)
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, X, Trash2, UserSquare2 } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useIsMobile } from '@/hooks/useMediaQuery';
import { useMapRegions } from '@/context/MapRegionsContext';
import { regionKeyForScope, type MapRegionCatalog } from '@/api/geoRegions';
import type {
  CandidateListItem,
  CandidateRead,
  CandidateScopeInput,
  CandidateScopeKind,
  CandidateScopeRead,
  CandidateSocialInput,
  CandidateSocialPlatform,
} from '@/types/candidate';
import {
  CANDIDATE_SOCIAL_PLATFORMS,
  CANDIDATE_SOCIAL_PLATFORM_LABELS,
} from '@/types/candidate';
import {
  listCandidates,
  getCandidate,
  createCandidate,
  updateCandidate,
  deactivateCandidate,
  reactivateCandidate,
  type CandidateCreate,
  type CandidateUpdate,
} from '@/api/candidates';

// ---------------------------------------------------------------------------
// Local, editable scope-row model, mirroring UsersAdminView's.
// ---------------------------------------------------------------------------
interface ScopeRow {
  kind: CandidateScopeKind;
  /** For kind==='ld' this is the LD string; for 'subregion' it's the turf key. */
  value: string;
}

/**
 * Map a resolved server scope back into an editable row.
 *
 * The turf catalog is threaded in rather than imported — it is tenant data
 * (`GET /geo/regions`) held in React state. `regionKeyForScope` owns the
 * set-comparison that used to live here, so the users admin and this one
 * cannot drift apart on what counts as the same turf.
 */
function scopeToRow(
  regions: MapRegionCatalog,
  scope: CandidateScopeRead,
): ScopeRow {
  if (scope.kind === 'ld') return { kind: 'ld', value: String(scope.ld) };
  const key = regionKeyForScope(regions, scope.ld, scope.precincts);
  return { kind: 'subregion', value: key ?? regions.keys[0] ?? '' };
}

/**
 * Build the API write payload from the editable rows.
 *
 * Two server-side rules shape this: 'ld' scopes FORBID `precincts` (a stray
 * list is a 422, not a silent ignore), and precincts must be STRINGS —
 * the catalog keeps the wire copy so nothing here re-coerces them.
 */
function rowsToScopeInputs(
  regions: MapRegionCatalog,
  rows: ScopeRow[],
): CandidateScopeInput[] {
  const out: CandidateScopeInput[] = [];
  for (const row of rows) {
    if (row.kind === 'ld' && row.value) {
      // No `precincts` key at all — forbidden for kind='ld'.
      out.push({ kind: 'ld', ld: String(row.value) });
    } else if (row.kind === 'subregion' && row.value) {
      const region = regions.byKey[row.value];
      if (region) {
        out.push({
          kind: 'subregion',
          ld: String(region.ld),
          precincts: region.precinctsAsStrings,
        });
      }
    }
  }
  return out;
}

/**
 * Suggest a slug from a full name. Only ever used to prefill on create — the
 * user can override, and it is never recomputed on edit.
 */
function slugify(name: string): string {
  return name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

function extractError(err: unknown, fallback: string): string {
  const resp = (err as any)?.response;
  if (resp?.status === 403) {
    return (
      resp?.data?.detail ||
      'You do not have permission for this action. Setting a candidate’s reach requires an administrator.'
    );
  }
  const detail = resp?.data?.detail;
  if (Array.isArray(detail)) {
    // FastAPI 422 validation errors come back as a list of {loc, msg}.
    return detail.map((d: any) => `${(d.loc ?? []).slice(1).join('.')}: ${d.msg}`).join('; ');
  }
  return detail || (err as any)?.message || fallback;
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  // Date-only strings (YYYY-MM-DD) parse as UTC midnight and can render as the
  // previous day west of Greenwich — pin them to local noon before formatting.
  const d = /^\d{4}-\d{2}-\d{2}$/.test(iso) ? new Date(`${iso}T12:00:00`) : new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString();
}

/**
 * Summarize a candidate's reach for the list.
 *
 * ⚠️ Zero scopes on a candidate means **county-wide office**, not "unscoped".
 * Say so explicitly — a blank cell here would read as missing data, and the
 * distinction is exactly the one plan §5.1 warns must never blur.
 *
 * Uses the server-precomputed `label` ("LD 12 — precincts 8, 35") rather than
 * re-deriving one, so candidate reach and user turf read identically.
 */
function reachSummary(scopes: CandidateScopeRead[] | undefined): string {
  if (!scopes || scopes.length === 0) return 'County-wide';
  return scopes.map((s) => s.label).join('; ');
}

// ---------------------------------------------------------------------------
// Small presentational bits.
// ---------------------------------------------------------------------------
function PartyBadge({ party }: { party: string | null }) {
  if (!party) return <span className="text-fg-muted">—</span>;
  return (
    <span className="inline-block rounded-full bg-bg-base px-2 py-0.5 text-xs font-semibold text-fg-secondary border border-border-subtle">
      {party}
    </span>
  );
}

function ActiveToggle({
  isActive,
  busy,
  onToggle,
}: {
  isActive: boolean;
  busy: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      onClick={onToggle}
      disabled={busy}
      className={
        'rounded-full px-2 py-0.5 text-xs font-semibold disabled:opacity-50 ' +
        (isActive
          ? 'bg-status-success-bg/30 text-status-success'
          : 'bg-bg-base text-fg-muted border border-border-subtle')
      }
      // Never say "delete" — the row is retained so historical knock leans
      // still resolve to a name.
      title={isActive ? 'Click to deactivate' : 'Click to reactivate'}
    >
      {isActive ? 'Active' : 'Inactive'}
    </button>
  );
}

// ===========================================================================
// Drawer — create/edit form.
// ===========================================================================
interface DrawerState {
  mode: 'create' | 'edit';
  /** Set for edit; the full record is fetched by id before the form hydrates. */
  candidateId: number | null;
}

interface CandidateDrawerProps {
  state: DrawerState;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}

/** The form body, mounted only once `candidate` is resolved (or null on create). */
function CandidateForm({
  candidate,
  isAdmin,
  onClose,
  onSaved,
}: {
  candidate: CandidateRead | null;
  isAdmin: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const editing = candidate !== null;
  const c = candidate;
  // The drawer above only mounts once the turf catalog has resolved, so the
  // scope-row initializer below can read it on first render.
  const { catalog: regions } = useMapRegions();

  const [slug, setSlug] = useState(c?.slug ?? '');
  const [slugTouched, setSlugTouched] = useState(editing);
  const [fullName, setFullName] = useState(c?.full_name ?? '');
  const [displayName, setDisplayName] = useState(c?.display_name ?? '');
  const [office, setOffice] = useState(c?.office ?? '');
  const [districtLabel, setDistrictLabel] = useState(c?.district_label ?? '');
  const [party, setParty] = useState(c?.party ?? '');
  const [electionDate, setElectionDate] = useState(c?.election_date ?? '');
  const [displayOrder, setDisplayOrder] = useState(String(c?.display_order ?? 0));
  const [isActive, setIsActive] = useState(c?.is_active ?? true);
  const [bio, setBio] = useState(c?.bio ?? '');
  const [websiteUrl, setWebsiteUrl] = useState(c?.website_url ?? '');
  const [donateUrl, setDonateUrl] = useState(c?.donate_url ?? '');
  const [socials, setSocials] = useState<CandidateSocialInput[]>(
    (c?.socials ?? []).map((s) => ({
      platform: s.platform,
      handle: s.handle ?? '',
      url: s.url,
      display_order: s.display_order,
    })),
  );
  const [scopeRows, setScopeRows] = useState<ScopeRow[]>(() =>
    (c?.scopes ?? []).map((scope) => scopeToRow(regions, scope)),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Holds the server's 409 refusal text while we ask the admin to confirm a
  // county-wide reach. Separate from `error` because it is answerable: it
  // renders with a confirm button, not as a dead end.
  const [countyWideConfirm, setCountyWideConfirm] = useState<string | null>(null);

  // Prefill the slug from the name until the user takes it over. Create only —
  // an existing slug is never recomputed.
  const onFullNameChange = (v: string) => {
    setFullName(v);
    if (!editing && !slugTouched) setSlug(slugify(v));
  };

  const addScopeRow = () =>
    setScopeRows((r) => [...r, { kind: 'subregion', value: regions.keys[0] ?? '' }]);
  const removeScopeRow = (i: number) => setScopeRows((r) => r.filter((_, idx) => idx !== i));
  const updateScopeRow = (i: number, next: Partial<ScopeRow>) =>
    setScopeRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...next } : row)));

  const addSocial = () =>
    setSocials((s) => [...s, { platform: 'x', handle: '', url: '', display_order: s.length }]);
  const removeSocial = (i: number) => setSocials((s) => s.filter((_, idx) => idx !== i));
  const updateSocial = (i: number, next: Partial<CandidateSocialInput>) =>
    setSocials((s) => s.map((row, idx) => (idx === i ? { ...row, ...next } : row)));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await save(false);
  };

  /**
   * `countyWideConfirmed` is only ever true on the retry that follows an
   * explicit confirmation of the 409 below — never on a first submit.
   */
  const save = async (countyWideConfirmed: boolean) => {
    setError(null);
    setCountyWideConfirm(null);

    if (!editing && !slug.trim()) {
      setError('A slug is required. It is the stable identifier and cannot be changed later.');
      return;
    }

    // url is required server-side; drop rows the user started but left blank
    // rather than eating a 422.
    const cleanSocials = socials
      .filter((s) => s.url.trim())
      .map((s, i) => ({
        platform: s.platform,
        handle: s.handle?.trim() ? s.handle.trim() : null,
        url: s.url.trim(),
        display_order: i,
      }));

    const scopes = rowsToScopeInputs(regions, scopeRows);
    const parsedOrder = Number(displayOrder);
    const orderNum = Number.isFinite(parsedOrder) ? parsedOrder : 0;

    setSaving(true);
    try {
      if (editing && c) {
        const payload: CandidateUpdate = {
          full_name: fullName,
          display_name: displayName.trim() || null,
          office,
          district_label: districtLabel.trim() || null,
          party: party.trim() || null,
          election_date: electionDate || null,
          is_active: isActive,
          display_order: orderNum,
          bio: bio.trim() || null,
          website_url: websiteUrl.trim() || null,
          donate_url: donateUrl.trim() || null,
          socials: cleanSocials,
        };
        // Only send scopes when the caller may set them. Omitting the key
        // leaves reach untouched; sending `[]` would WIPE it (replace-all on
        // present), and a non-empty list from a non-admin is a 403.
        if (isAdmin) payload.scopes = scopes;
        // Emptying reach on a candidate who HAS a login widens that login to
        // the whole county, so the server 409s unless a human confirmed it.
        // The flag rides only on the post-confirmation retry.
        if (countyWideConfirmed) payload.county_wide_confirmed = true;
        await updateCandidate(c.id, payload);
      } else {
        // No `is_active` on create — the schema has no such field and a new
        // candidate is always created active.
        const payload: CandidateCreate = {
          slug: slug.trim(),
          full_name: fullName,
          display_name: displayName.trim() || null,
          office,
          district_label: districtLabel.trim() || null,
          party: party.trim() || null,
          election_date: electionDate || null,
          display_order: orderNum,
          bio: bio.trim() || null,
          website_url: websiteUrl.trim() || null,
          donate_url: donateUrl.trim() || null,
        };
        if (cleanSocials.length > 0) payload.socials = cleanSocials;
        if (isAdmin && scopes.length > 0) payload.scopes = scopes;
        await createCandidate(payload);
      }
      onSaved();
    } catch (err) {
      // A 409 here is the reach reconcile refusing to strand a candidate
      // login county-wide. That is occasionally the correct edit — a Sheriff
      // or an Assessor really does run county-wide — so surface it as a
      // question rather than dead-ending the admin on an error they cannot
      // clear. Anything else, including a second 409, is a real failure.
      const resp = (err as any)?.response;
      if (resp?.status === 409 && !countyWideConfirmed) {
        setCountyWideConfirm(
          resp?.data?.detail ||
            'This candidate has a login. Removing their reach would let it see the entire county.',
        );
      } else {
        setError(extractError(err, 'Could not save the candidate. Please try again.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    'w-full rounded-md border border-border-subtle bg-bg-base px-3 py-2 text-sm text-fg-primary placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-accent';
  const labelCls = 'mb-1 block text-xs font-semibold text-fg-secondary';

  return (
    <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-4 px-5 py-4">
      <div>
        <label className={labelCls} htmlFor="cand-full-name">
          Full name (as on the ballot)
        </label>
        <input
          id="cand-full-name"
          type="text"
          required
          value={fullName}
          onChange={(e) => onFullNameChange(e.target.value)}
          className={inputCls}
          placeholder="Jane Smith"
        />
      </div>

      <div>
        <label className={labelCls} htmlFor="cand-slug">
          Slug
        </label>
        <input
          id="cand-slug"
          type="text"
          required
          value={slug}
          disabled={editing}
          onChange={(e) => {
            setSlugTouched(true);
            setSlug(e.target.value);
          }}
          className={inputCls + (editing ? ' opacity-60' : '')}
          placeholder="jane-smith"
        />
        <p className="mt-1 text-xs text-fg-muted">
          {editing ? (
            <>
              The slug is permanent. Past door knocks resolve their candidate through it, so
              changing it would silently re-attribute history. Renaming the candidate above is
              free — for a different person, create a new candidate.
            </>
          ) : (
            <>
              Stable identifier, lowercase-kebab. <strong>It cannot be changed later</strong> and
              must never be reused for a different person.
            </>
          )}
        </p>
      </div>

      <div>
        <label className={labelCls} htmlFor="cand-display-name">
          Short name (optional)
        </label>
        <input
          id="cand-display-name"
          type="text"
          value={displayName ?? ''}
          onChange={(e) => setDisplayName(e.target.value)}
          className={inputCls}
          placeholder="Smith"
        />
        <p className="mt-1 text-xs text-fg-muted">
          What a volunteer says at the door. Falls back to the full name.
        </p>
      </div>

      <div>
        <label className={labelCls} htmlFor="cand-office">
          Office
        </label>
        <input
          id="cand-office"
          type="text"
          required
          value={office}
          onChange={(e) => setOffice(e.target.value)}
          className={inputCls}
          placeholder="State Representative"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls} htmlFor="cand-party">
            Party
          </label>
          <input
            id="cand-party"
            type="text"
            value={party ?? ''}
            onChange={(e) => setParty(e.target.value)}
            className={inputCls}
            placeholder="Republican"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="cand-election-date">
            Election date
          </label>
          <input
            id="cand-election-date"
            type="date"
            value={electionDate ?? ''}
            onChange={(e) => setElectionDate(e.target.value)}
            className={inputCls}
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls} htmlFor="cand-district-label">
            District label
          </label>
          <input
            id="cand-district-label"
            type="text"
            value={districtLabel ?? ''}
            onChange={(e) => setDistrictLabel(e.target.value)}
            className={inputCls}
            placeholder="LD 12"
          />
          <p className="mt-1 text-xs text-fg-muted">Display only — reach is set below.</p>
        </div>
        <div>
          <label className={labelCls} htmlFor="cand-display-order">
            Display order
          </label>
          <input
            id="cand-display-order"
            type="number"
            value={displayOrder}
            onChange={(e) => setDisplayOrder(e.target.value)}
            className={inputCls}
          />
          <p className="mt-1 text-xs text-fg-muted">Lower sorts first.</p>
        </div>
      </div>

      <div>
        <label className={labelCls} htmlFor="cand-bio">
          Bio (optional)
        </label>
        <textarea
          id="cand-bio"
          value={bio ?? ''}
          rows={3}
          onChange={(e) => setBio(e.target.value)}
          className={inputCls}
          placeholder="Short background for the canvasser briefing."
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={labelCls} htmlFor="cand-website">
            Website
          </label>
          <input
            id="cand-website"
            type="url"
            value={websiteUrl ?? ''}
            onChange={(e) => setWebsiteUrl(e.target.value)}
            className={inputCls}
            placeholder="https://…"
          />
        </div>
        <div>
          <label className={labelCls} htmlFor="cand-donate">
            Donate URL
          </label>
          <input
            id="cand-donate"
            type="url"
            value={donateUrl ?? ''}
            onChange={(e) => setDonateUrl(e.target.value)}
            className={inputCls}
            placeholder="https://…"
          />
        </div>
      </div>

      {editing && (
        <label className="flex items-center gap-2 text-sm text-fg-secondary">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            className="h-4 w-4 accent-accent"
          />
          Active
        </label>
      )}

      {/* Socials editor ------------------------------------------------ */}
      <div className="rounded-md border border-border-subtle bg-bg-base p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold text-fg-secondary">Social links</span>
          <button
            type="button"
            onClick={addSocial}
            className="inline-flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-semibold text-white hover:bg-accent-hover"
          >
            <Plus size={12} /> Add link
          </button>
        </div>

        {socials.length === 0 ? (
          <p className="text-xs text-fg-muted">No social links.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {socials.map((s, i) => (
              <div key={i} className="flex flex-wrap items-center gap-2">
                <select
                  value={s.platform}
                  onChange={(e) =>
                    updateSocial(i, { platform: e.target.value as CandidateSocialPlatform })
                  }
                  className="rounded border border-border-subtle bg-bg-raised px-2 py-1 text-xs text-fg-primary"
                  aria-label="Platform"
                >
                  {CANDIDATE_SOCIAL_PLATFORMS.map((p) => (
                    <option key={p} value={p}>
                      {CANDIDATE_SOCIAL_PLATFORM_LABELS[p]}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={s.handle ?? ''}
                  onChange={(e) => updateSocial(i, { handle: e.target.value })}
                  className="w-24 rounded border border-border-subtle bg-bg-raised px-2 py-1 text-xs text-fg-primary"
                  placeholder="@handle"
                  aria-label="Handle"
                />
                <input
                  type="url"
                  value={s.url}
                  onChange={(e) => updateSocial(i, { url: e.target.value })}
                  className="min-w-0 flex-1 rounded border border-border-subtle bg-bg-raised px-2 py-1 text-xs text-fg-primary"
                  placeholder="https://…"
                  aria-label="URL"
                />
                <button
                  type="button"
                  onClick={() => removeSocial(i)}
                  className="rounded p-1 text-fg-muted hover:bg-bg-raised hover:text-status-danger"
                  aria-label="Remove link"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reach editor -------------------------------------------------- */}
      <div className="rounded-md border border-border-subtle bg-bg-base p-3">
        <div className="mb-2 flex items-center justify-between">
          <span className="text-xs font-semibold text-fg-secondary">Reach (district covered)</span>
          <button
            type="button"
            onClick={addScopeRow}
            // With no turfs loaded there is nothing a reach row could name,
            // and a row whose select is empty is dropped on submit — leaving
            // the candidate county-wide without anyone choosing that.
            disabled={!isAdmin || regions.isEmpty}
            className="inline-flex items-center gap-1 rounded bg-accent px-2 py-1 text-xs font-semibold text-white hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Plus size={12} /> Add turf
          </button>
        </div>

        {!isAdmin ? (
          <p className="text-xs text-fg-muted">
            A candidate’s reach can only be set by an administrator. This candidate keeps its
            current reach: <strong>{reachSummary(c?.scopes)}</strong>.
          </p>
        ) : regions.isEmpty ? (
          <p className="text-xs text-fg-muted">
            No turfs are loaded for this tenant yet, so a reach cannot be set.
            Every candidate here reads as <strong>county-wide</strong> until
            they are. Contact platform support to load them.
          </p>
        ) : scopeRows.length === 0 ? (
          <p className="text-xs text-fg-muted">
            No turfs — this is a <strong>county-wide</strong> office (Sheriff, Assessor). Add a
            turf for a district race.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {scopeRows.map((row, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  value={row.kind}
                  onChange={(e) => {
                    const kind = e.target.value as CandidateScopeKind;
                    const value =
                      kind === 'ld'
                        ? regions.ldOptions[0] ?? ''
                        : regions.keys[0] ?? '';
                    updateScopeRow(i, { kind, value });
                  }}
                  className="rounded border border-border-subtle bg-bg-raised px-2 py-1 text-xs text-fg-primary"
                  aria-label="Scope kind"
                >
                  <option value="subregion">Subregion</option>
                  <option value="ld">Whole LD</option>
                </select>

                {row.kind === 'ld' ? (
                  <select
                    value={row.value}
                    onChange={(e) => updateScopeRow(i, { value: e.target.value })}
                    className="min-w-0 flex-1 rounded border border-border-subtle bg-bg-raised px-2 py-1 text-xs text-fg-primary"
                    aria-label="Legislative district"
                  >
                    {regions.ldOptions.map((ld) => (
                      <option key={ld} value={ld}>
                        LD {ld}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={row.value}
                    onChange={(e) => updateScopeRow(i, { value: e.target.value })}
                    className="min-w-0 flex-1 rounded border border-border-subtle bg-bg-raised px-2 py-1 text-xs text-fg-primary"
                    aria-label="Subregion"
                  >
                    {regions.keys.map((k) => (
                      <option key={k} value={k}>
                        {k} — {regions.byKey[k].name} (LD {regions.byKey[k].ld})
                      </option>
                    ))}
                  </select>
                )}

                <button
                  type="button"
                  onClick={() => removeScopeRow(i)}
                  className="rounded p-1 text-fg-muted hover:bg-bg-raised hover:text-status-danger"
                  aria-label="Remove turf"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {error && (
        <div className="rounded-md border border-status-danger-bg/30 bg-status-danger-bg/20 px-3 py-2 text-sm text-status-danger">
          {error}
        </div>
      )}

      {countyWideConfirm && (
        <div className="rounded-md border border-status-warning/40 bg-status-warning/10 px-3 py-2 text-sm text-status-warning">
          <p className="font-semibold">This makes the reach county-wide.</p>
          <p className="mt-1">{countyWideConfirm}</p>
          <div className="mt-2 flex items-center gap-2">
            <button
              type="button"
              disabled={saving}
              onClick={() => void save(true)}
              className="rounded-md border border-status-warning/40 px-2.5 py-1.5 text-xs font-semibold hover:bg-status-warning/20 disabled:opacity-50"
            >
              Yes, county-wide
            </button>
            <button
              type="button"
              onClick={() => setCountyWideConfirm(null)}
              className="rounded-md px-2.5 py-1.5 text-xs font-semibold text-fg-secondary hover:bg-bg-base"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="mt-auto flex items-center justify-end gap-2 border-t border-border-subtle pt-4">
        <button
          type="button"
          onClick={onClose}
          className="rounded-md border border-border-subtle px-3 py-2 text-sm font-semibold text-fg-secondary hover:bg-bg-base"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={saving}
          className="rounded-md bg-accent px-4 py-2 text-sm font-semibold text-white hover:bg-accent-hover disabled:opacity-50"
        >
          {saving ? 'Saving…' : editing ? 'Save changes' : 'Create candidate'}
        </button>
      </div>
    </form>
  );
}

function CandidateDrawer({ state, isAdmin, onClose, onSaved }: CandidateDrawerProps) {
  const editing = state.mode === 'edit';
  const [full, setFull] = useState<CandidateRead | null>(null);
  const [loading, setLoading] = useState(editing);
  const [loadError, setLoadError] = useState<string | null>(null);

  // The list row is the LIGHT shape — re-fetch the full record before
  // hydrating, or bio/socials/urls would be blanked on save.
  useEffect(() => {
    let cancelled = false;
    if (!editing || state.candidateId === null) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const data = await getCandidate(state.candidateId!);
        if (!cancelled) setFull(data);
      } catch (err) {
        if (!cancelled) setLoadError(extractError(err, 'Could not load this candidate.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [editing, state.candidateId]);

  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 flex h-full w-full max-w-md flex-col overflow-y-auto bg-bg-raised shadow-xl">
        <div className="flex items-center justify-between border-b border-border-subtle px-5 py-4">
          <h2 className="text-base font-semibold text-fg-primary">
            {editing ? 'Edit candidate' : 'New candidate'}
          </h2>
          <button
            onClick={onClose}
            className="rounded p-1 text-fg-muted hover:bg-bg-base hover:text-fg-primary"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {loading ? (
          <div className="px-5 py-8 text-sm text-fg-muted">Loading candidate…</div>
        ) : loadError ? (
          <div className="px-5 py-8">
            <div className="rounded-md border border-status-danger-bg/30 bg-status-danger-bg/20 px-3 py-2 text-sm text-status-danger">
              {loadError}
            </div>
          </div>
        ) : (
          <CandidateForm
            candidate={full}
            isAdmin={isAdmin}
            onClose={onClose}
            onSaved={onSaved}
          />
        )}
      </div>
    </div>
  );
}

// ===========================================================================
// Main view.
// ===========================================================================
export function CandidatesAdminView() {
  // Held so the drawer's one-shot scope-row initializer sees a real catalog.
  const { loading: regionsLoading } = useMapRegions();
  const { user } = useAuth();
  const isAdmin = (user?.role ?? '').toString().toUpperCase() === 'ADMIN';
  const isMobile = useIsMobile();

  const [candidates, setCandidates] = useState<CandidateListItem[] | null>(null);
  const [showInactive, setShowInactive] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Two fetches, not one: `?is_active=` (the endpoint's documented "all"
      // mode) actually 422s — FastAPI can't parse an empty string into
      // Optional[bool], so the None branch is unreachable. Measured, not
      // assumed. See `src/api/candidates.ts`.
      const active = await listCandidates(true);
      const inactive = showInactive ? await listCandidates(false) : [];
      const merged = [...active, ...inactive].sort(
        (a, b) => a.display_order - b.display_order || a.full_name.localeCompare(b.full_name),
      );
      setCandidates(merged);
    } catch (err) {
      setError(extractError(err, 'Could not load candidates.'));
      setCandidates(null);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleActive = async (c: CandidateListItem) => {
    setRowError(null);
    setBusyId(c.id);
    try {
      if (c.is_active) {
        await deactivateCandidate(c.id);
      } else {
        await reactivateCandidate(c.id);
      }
      await load();
    } catch (err) {
      setRowError(extractError(err, 'Could not update the candidate.'));
    } finally {
      setBusyId(null);
    }
  };

  const onSaved = useCallback(() => {
    setDrawer(null);
    void load();
  }, [load]);

  const content = useMemo(() => {
    if (loading) {
      return <div className="py-16 text-center text-fg-muted">Loading candidates…</div>;
    }
    if (error) {
      return (
        <div className="mx-auto max-w-2xl rounded-lg border border-status-danger-bg/30 bg-status-danger-bg/20 p-6">
          <h2 className="mb-1 text-lg font-semibold text-status-danger">
            Couldn’t load candidates
          </h2>
          <p className="text-fg-secondary">{error}</p>
          <button
            onClick={() => void load()}
            className="mt-3 rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-white hover:bg-accent-hover"
          >
            Retry
          </button>
        </div>
      );
    }
    if (!candidates || candidates.length === 0) {
      return (
        <div className="py-16 text-center text-fg-muted">
          No candidates yet. Use “New candidate” to add the first one.
        </div>
      );
    }

    // Mobile: stack each row into a card. An 8-column table is unreadable at
    // 375px and horizontal scrolling loses the name column.
    if (isMobile) {
      return (
        <div className="flex flex-col gap-3">
          {candidates.map((c) => (
            <div
              key={c.id}
              className={
                'rounded-lg border border-border-subtle bg-bg-raised p-4 ' +
                (c.is_active ? '' : 'opacity-60')
              }
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="truncate font-semibold text-fg-primary">{c.full_name}</div>
                  <div className="truncate text-sm text-fg-secondary">{c.office}</div>
                </div>
                <ActiveToggle
                  isActive={c.is_active}
                  busy={busyId === c.id}
                  onToggle={() => void toggleActive(c)}
                />
              </div>
              <dl className="mt-3 grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-xs">
                <dt className="text-fg-muted">Slug</dt>
                <dd className="truncate font-mono text-fg-secondary">{c.slug}</dd>
                <dt className="text-fg-muted">Party</dt>
                <dd>
                  <PartyBadge party={c.party} />
                </dd>
                <dt className="text-fg-muted">Reach</dt>
                <dd className="text-fg-secondary">{reachSummary(c.scopes)}</dd>
                <dt className="text-fg-muted">Election</dt>
                <dd className="text-fg-secondary">{fmtDate(c.election_date)}</dd>
              </dl>
              <button
                onClick={() => setDrawer({ mode: 'edit', candidateId: c.id })}
                className="mt-3 w-full rounded-md border border-border-subtle px-2.5 py-2 text-xs font-semibold text-fg-secondary hover:bg-bg-base"
              >
                Edit
              </button>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="overflow-x-auto rounded-lg border border-border-subtle">
        <table className="w-full text-sm">
          <thead className="bg-bg-base text-left text-xs uppercase text-fg-muted">
            <tr>
              <th className="px-4 py-2 font-semibold">Name</th>
              <th className="px-4 py-2 font-semibold">Slug</th>
              <th className="px-4 py-2 font-semibold">Office</th>
              <th className="px-4 py-2 font-semibold">Party</th>
              <th className="px-4 py-2 font-semibold">Reach</th>
              <th className="px-4 py-2 font-semibold">Election</th>
              <th className="px-4 py-2 font-semibold">Active</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border-subtle">
            {candidates.map((c) => (
              <tr key={c.id} className={c.is_active ? '' : 'opacity-60'}>
                <td className="px-4 py-2 text-fg-primary">
                  {c.full_name}
                  {c.display_name && (
                    <span className="ml-1 text-xs text-fg-muted">({c.display_name})</span>
                  )}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-fg-muted">{c.slug}</td>
                <td className="px-4 py-2 text-fg-secondary">
                  {c.office}
                  {c.district_label && (
                    <span className="ml-1 text-xs text-fg-muted">· {c.district_label}</span>
                  )}
                </td>
                <td className="px-4 py-2">
                  <PartyBadge party={c.party} />
                </td>
                <td className="px-4 py-2 text-fg-secondary">{reachSummary(c.scopes)}</td>
                <td className="px-4 py-2 text-fg-muted">{fmtDate(c.election_date)}</td>
                <td className="px-4 py-2">
                  <ActiveToggle
                    isActive={c.is_active}
                    busy={busyId === c.id}
                    onToggle={() => void toggleActive(c)}
                  />
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => setDrawer({ mode: 'edit', candidateId: c.id })}
                    className="rounded-md border border-border-subtle px-2.5 py-1 text-xs font-semibold text-fg-secondary hover:bg-bg-base"
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, candidates, busyId, load, isMobile]);

  return (
    <div className="min-h-dvh bg-bg-base p-4 text-fg-primary md:p-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <UserSquare2 size={20} className="shrink-0 text-accent" />
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-fg-primary">Candidates</h1>
              <p className="text-sm text-fg-muted">
                The people a door knock is for — name, office, and district reach.
              </p>
            </div>
          </div>
          <button
            onClick={() => setDrawer({ mode: 'create', candidateId: null })}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-accent px-3 py-2 text-sm font-semibold text-white hover:bg-accent-hover"
          >
            <Plus size={16} /> New candidate
          </button>
        </div>

        <div className="mb-4 flex flex-wrap items-center gap-x-4 gap-y-2">
          <label className="flex items-center gap-2 text-sm text-fg-secondary">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="h-4 w-4 accent-accent"
            />
            Show inactive
          </label>
          <p className="text-xs text-fg-muted">
            Candidates are deactivated, never deleted — past knocks still resolve their name.
          </p>
        </div>

        {!isAdmin && (
          <div className="mb-4 rounded-md border border-border-subtle bg-bg-raised px-4 py-2 text-sm text-fg-secondary">
            You can manage candidates, but setting a candidate’s district reach requires an
            administrator.
          </div>
        )}

        {rowError && (
          <div className="mb-4 rounded-md border border-status-danger-bg/30 bg-status-danger-bg/20 px-4 py-2 text-sm text-status-danger">
            {rowError}
          </div>
        )}

        {content}
      </div>

      {drawer && !regionsLoading && (
        <CandidateDrawer
          state={drawer}
          isAdmin={isAdmin}
          onClose={() => setDrawer(null)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}

export default CandidatesAdminView;
